import { useFetchPatientInfo } from '@/api/hooks/useFetchPatientInfo'
import { useUpdatePatientProfile } from '@/api/hooks/useUpdatePatientProfile'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { Input } from '@/ui/core/input'
import { SheetTitle } from '@/ui/core/sheet'
import { HStack, VStack } from '@/ui/core/stack'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { Controller, useForm } from 'react-hook-form'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'
import { check, minLength, object, pipe, string, trim } from 'valibot'
import validator from 'validator'
import type { ProfileFormData } from './ProfileInfoForm.types'

const ProfileSchema = object({
	firstName: pipe(string(), trim(), minLength(1, 'First name is required')),
	lastName: pipe(string(), trim(), minLength(1, 'Last name is required')),
	phone: pipe(
		string(),
		check(
			(value: string) => validator.isMobilePhone(value) as boolean,
			'Invalid phone number'
		)
	),
})

const ProfileInfoForm = ({ onSaved }: { onSaved?: () => void } = {}) => {
	const { patientInfo } = useFetchPatientInfo()
	const { mutate: updatePatientInfo } = useUpdatePatientProfile()

	const {
		control,
		handleSubmit,
		formState: { isLoading, isValid },
	} = useForm<ProfileFormData>({
		resolver: valibotResolver(ProfileSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			phone: '',
		},
		values: patientInfo,
		resetOptions: {
			keepDirtyValues: true,
		},
	})

	const onFormSubmit = async ({ firstName, lastName, phone }: ProfileFormData) => {
		analytics.trackEvent('updating-profile')

		updatePatientInfo(
			{ firstName, lastName, phone },
			{
				onSuccess: () => {
					Toast.show({
						type: 'success',
						text1: 'Profile information updated!',
					})
					onSaved?.()
				},
				onError: () => {
					Toast.show({
						type: 'error',
						text1: 'We ran into an issue updating your profile, please try again',
					})
				},
			}
		)
	}

	return (
		<View className="pt-2 pb-2">
			<SheetTitle glyph="crown">Personal Details</SheetTitle>
			<VStack gap={20}>
				<VStack gap={12}>
					<HStack fullWidth gap={12}>
						<Controller
							name="firstName"
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Input
									className="flex-1"
									placeholder="First name"
									textContentType="givenName"
									autoComplete="given-name"
									returnKeyType="next"
									value={value}
									onBlur={onBlur}
									onChangeText={onChange}
								/>
							)}
						/>
						<Controller
							name="lastName"
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Input
									className="flex-1"
									placeholder="Last name"
									textContentType="familyName"
									autoComplete="family-name"
									returnKeyType="next"
									value={value}
									onBlur={onBlur}
									onChangeText={onChange}
								/>
							)}
						/>
					</HStack>
					<Controller
						name="phone"
						control={control}
						render={({ field: { onChange, onBlur, value } }) => (
							<Input
								className="w-full"
								placeholder="Phone"
								textContentType="telephoneNumber"
								autoComplete="tel"
								returnKeyType="done"
								value={value}
								onBlur={onBlur}
								onChangeText={onChange}
								inputMode="tel"
							/>
						)}
					/>
				</VStack>
				<Button
					fullWidth
					size="lg"
					isLoading={isLoading}
					disabled={!isValid}
					onPress={handleSubmit(onFormSubmit)}
				>
					<ButtonText>Save</ButtonText>
				</Button>
			</VStack>
		</View>
	)
}

export { ProfileInfoForm }
